import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { subscriptionService } from '../../../../services/subscription/subscriptionService';
import { useSubscription } from '../../../../context/subscription/SubscriptionContext';
import MainLayout from '../../../../Layout/MainLayout';
import PageContainer from '../../../../components/layout/PageContainer';
import SubscriptionSkeleton from './sections/SubscriptionSkeleton';
import SubscriptionHeader from './sections/SubscriptionHeader';
import SubscriptionStandaloneBanners from './sections/SubscriptionStandaloneBanners';
import SubscriptionPricingGrid from './sections/SubscriptionPricingGrid';
import SubscriptionComparisonTable from './sections/SubscriptionComparisonTable';
import SubscriptionFaqs from './sections/SubscriptionFaqs';
import SubscriptionModals from './sections/SubscriptionModals';
import subscriptionData from '../../../../data/subscription/subscriptionData.json';

const FEATURE_CATEGORY_ORDER = subscriptionData.featureCategoryOrder;
const REFERENCE_FEATURES = subscriptionData.referenceFeatures;

const formatPrice = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);


const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [billingInterval, setBillingInterval] = useState('month'); // 'month' | 'year'
  const { subscription, trialDaysRemaining, isTrialPendingVoucher, refreshSubscription } = useSubscription();

  // Request Modal State for Downgrades & Cycle Switches
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedTargetPlan, setSelectedTargetPlan] = useState(null);
  const [targetInterval, setTargetInterval] = useState('month');
  const [requestType, setRequestType] = useState('downgrade');
  const [requestReason, setRequestReason] = useState('Pricing is too high / not cost effective');
  const [requestComments, setRequestComments] = useState('');
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [requestSuccessMessage, setRequestSuccessMessage] = useState(null);

  // Mid-Cycle Upgrade Modal State
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null);
  const [isUpgrading, setIsUpgrading] = useState(false);

  // Dedicated Checkout Modal State
  const [showCheckoutPage, setShowCheckoutPage] = useState(false);

  useEffect(() => {
    const fetchPlans = async () => {
      try { 
        const data = await subscriptionService.getAvailablePlans(); 
        setPlans(data.plans || []); 
      }
      catch (error) { 
        toast.error('Failed to load subscription plans'); 
      }
      finally { 
        setLoading(false); 
      }
    };
    fetchPlans();
  }, []);

  const currentPlanObj = useMemo(() => {
    if (!subscription?.plan_id) return null;
    if (typeof subscription.plan_id === 'object' && subscription.plan_id.slug) {
      return subscription.plan_id;
    }
    const targetId = typeof subscription.plan_id === 'object' ? subscription.plan_id._id : subscription.plan_id;
    return plans.find(p => (p._id === targetId || p.slug === subscription?.plan_id?.slug)) || null;
  }, [subscription, plans]);

  const currentPlanSlug = currentPlanObj?.slug || (typeof subscription?.plan_id === 'object' ? subscription?.plan_id?.slug : null) || (subscription?.status === 'trial' ? 'growth' : null);
  const currentInterval = subscription?.billing_interval || 'month';
  const isPaidActive = subscription?.status === 'active' || subscription?.status === 'paused' || (subscription?.status !== 'trial' && subscription?.status !== 'expired' && !!subscription?.status);
  const cycleEndDate = subscription?.end_date || subscription?.next_billing_at || subscription?.nextBillingAt;
  const daysRemaining = cycleEndDate ? Math.max(1, Math.ceil((new Date(cycleEndDate) - new Date()) / (1000 * 60 * 60 * 24))) : 30;

  const slugRanks = { starter: 1, pro: 2, growth: 3, enterprise: 4 };
  const currentPlanRank = slugRanks[currentPlanSlug] || (subscription?.status === 'trial' ? 3 : 0);

  const categories = useMemo(() => FEATURE_CATEGORY_ORDER.map((category) => ({ 
    category, 
    features: REFERENCE_FEATURES.filter((feature) => feature.category === category) 
  })), []);

  const getFeature = (plan, feature) => {
    const value = feature.values[plan.slug] ?? '—';
    return { value, included: value === '✓' };
  };

  const handleOpenRequestModal = (plan, interval, type) => {
    if (subscription?.pending_change?.status === 'mandate_pending') {
      toast.error('You already have an approved plan change awaiting authorization. Please authorize the change or contact support.');
      return;
    }
    setSelectedTargetPlan(plan);
    setTargetInterval(interval);
    setRequestType(type);
    setRequestReason(
      type === 'downgrade' 
        ? 'Pricing is too high / not cost effective' 
        : type === 'upgrade' 
        ? 'Upgrading to higher annual tier'
        : 'Prefer monthly cashflow / flexibility'
    );
    setRequestComments('');
    setRequestSuccessMessage(null);
    setShowRequestModal(true);
  };

  const handleUpgradeClick = (plan) => {
    setSelectedUpgradePlan(plan);
    if (subscription?.billing_interval === 'year' || billingInterval === 'year') {
      handleOpenRequestModal(plan, 'year', 'upgrade');
      return;
    }
    navigate(`/checkout?plan=${plan._id}&interval=${billingInterval}&from=subscription`);
  };

  const handleConfirmUpgrade = () => {
    setShowUpgradeModal(false);
    setShowCheckoutPage(true);
  };

  const triggerRazorpayMandate = async (prefilledMethod) => {
    if (!selectedUpgradePlan) return;
    setIsUpgrading(true);
    try {
      const checkoutData = await subscriptionService.createCheckout(selectedUpgradePlan._id, 'month');

      const cycleEnd = subscription?.end_date || subscription?.next_billing_at || subscription?.nextBillingAt;
      const formattedCycleEnd = cycleEnd ? formatDate(cycleEnd) : 'your next cycle date';

      const rzp = new window.Razorpay({
        key: checkoutData.razorpay_key,
        subscription_id: checkoutData.subscription_id,
        name: 'Trrip',
        description: `Mandate verification (₹5 refundable today). First plan charge on ${formattedCycleEnd}`,
        prefill: {
          name: checkoutData.customer_name || '',
          email: checkoutData.customer_email || '',
          contact: checkoutData.customer_contact || '',
          method: prefilledMethod || undefined
        },
        handler: async (response) => {
          try {
            await subscriptionService.verifyPayment({
              razorpay_subscription_id: response.razorpay_subscription_id || checkoutData.subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: selectedUpgradePlan._id,
              billing_interval: 'month'
            });
            toast.success(`Mandate Authorized! Upgraded to ${selectedUpgradePlan.name} Tier.`);
            setShowCheckoutPage(false);
            refreshSubscription();
          } catch (verifyError) {
            toast.error('Payment mandate verification failed. Please try again.');
          } finally {
            setIsUpgrading(false);
          }
        },
        modal: {
          ondismiss: () => {
            setIsUpgrading(false);
          }
        }
      });
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Failed to initialize payment mandate');
      setIsUpgrading(false);
    }
  };

  const handleDirectDowngradeRequest = async (plan, interval, type = 'downgrade') => {
    if (!plan) return;
    if (subscription?.pending_change?.status === 'mandate_pending') {
      toast.error('You already have an approved plan change awaiting authorization. Please authorize the change or contact support.');
      return;
    }
    setSubscribing(plan._id);
    try {
      await subscriptionService.createSubscriptionRequest({
        type: type,
        target_plan_id: plan._id,
        target_billing_interval: interval,
        reason: type === 'downgrade'
          ? 'Pricing is too high / not cost effective'
          : type === 'switch_billing_cycle'
          ? (interval === 'month' ? 'Prefer monthly cashflow / flexibility' : 'Switching to annual billing')
          : 'Prefer monthly cashflow / flexibility',
        comments: ''
      });
      const msg = type === 'downgrade'
        ? `Your request to downgrade to ${plan.name} has been submitted successfully.`
        : type === 'switch_billing_cycle'
        ? `Your request to switch to ${interval === 'year' ? 'annual' : 'monthly'} billing for ${plan.name} has been submitted successfully. Our team will coordinate with you in 24–48 hours.`
        : type === 'annual_change'
        ? `Your plan change request for ${plan.name} has been submitted! Our team will coordinate with you in 24–48 hours.`
        : `Your request to switch billing for ${plan.name} has been submitted successfully.`;
      toast.success(msg);
      refreshSubscription();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Failed to submit plan change request');
    } finally {
      setSubscribing(null);
    }
  };

  const handleEnterpriseRequest = async (plan) => {
    if (!plan) return;
    if (subscription?.pending_change?.status === 'mandate_pending') {
      toast.error('You already have an approved plan change awaiting authorization. Please authorize the change or contact support.');
      return;
    }
    setSubscribing(plan._id);
    try {
      await subscriptionService.createSubscriptionRequest({
        type: 'custom',
        target_plan_id: plan._id,
        target_billing_interval: billingInterval,
        reason: 'Enterprise Custom Volume Request',
        comments: 'Agent requested custom Enterprise volume.'
      });
      toast.success('Your Enterprise plan request has been submitted! Our team will contact you within 24 hours.');
      refreshSubscription();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Failed to submit Enterprise request');
    } finally {
      setSubscribing(null);
    }
  };

  const handleSubmitSubscriptionRequest = async () => {
    if (!selectedTargetPlan) return;
    setIsSubmittingRequest(true);
    try {
      await subscriptionService.createSubscriptionRequest({
        type: requestType,
        target_plan_id: selectedTargetPlan._id,
        target_billing_interval: targetInterval,
        reason: requestReason,
        comments: requestComments
      });
      setRequestSuccessMessage(
        requestType === 'switch_billing_cycle'
          ? 'Your request to switch to monthly billing has been received. Our team will coordinate with you in 24–48 hours.'
          : requestType === 'upgrade'
          ? `Your request to upgrade to ${selectedTargetPlan.name} (Annual) has been received. Our team will coordinate with you in 24–48 hours.`
          : `Your downgrade request to ${selectedTargetPlan.name} has been received. Our team will coordinate with you in 24–48 hours.`
      );
      refreshSubscription();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Failed to submit request');
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const handleSubscribe = (plan) => {
    if (!plan) return;
    if (subscription?.pending_change?.status === 'mandate_pending') {
      const pending = subscription.pending_change;
      const targetPlanId = pending.target_plan_id?._id || pending.target_plan_id;
      const targetInterval = pending.target_billing_interval || 'month';
      navigate(`/checkout?plan=${targetPlanId}&interval=${targetInterval}&from=subscription`);
      return;
    }
    if (plan.is_custom || plan.slug === 'enterprise') { 
      handleEnterpriseRequest(plan);
      return; 
    }
    navigate(`/checkout?plan=${plan._id}&interval=${billingInterval}&from=subscription`);
  };

  if (loading) {
    return (
      <MainLayout activeTab="dashboard">
        <SubscriptionSkeleton />
      </MainLayout>
    );
  }

  return (
    <MainLayout activeTab="dashboard">
      <PageContainer className="font-sans text-slate-800">
        
        {/* Header & Toggle Section */}
        <SubscriptionHeader
          billingInterval={billingInterval}
          setBillingInterval={setBillingInterval}
        />

        {/* Banners */}
        <SubscriptionStandaloneBanners
          subscription={subscription}
          isTrialPendingVoucher={isTrialPendingVoucher}
          trialDaysRemaining={trialDaysRemaining}
          navigate={navigate}
        />

        {/* Pricing Cards Grid */}
        <SubscriptionPricingGrid
          plans={plans}
          billingInterval={billingInterval}
          subscription={subscription}
          isPaidActive={isPaidActive}
          currentPlanSlug={currentPlanSlug}
          currentInterval={currentInterval}
          daysRemaining={daysRemaining}
          slugRanks={slugRanks}
          currentPlanRank={currentPlanRank}
          subscribing={subscribing}
          handleSubscribe={handleSubscribe}
          handleUpgradeClick={handleUpgradeClick}
          handleDirectDowngradeRequest={handleDirectDowngradeRequest}
          handleEnterpriseRequest={handleEnterpriseRequest}
          formatPrice={formatPrice}
          navigate={navigate}
        />

        {/* Feature Comparison Table */}
        <SubscriptionComparisonTable
          plans={plans}
          categories={categories}
          getFeature={getFeature}
        />

        {/* FAQs */}
        <SubscriptionFaqs />

        {/* Modals */}
        <SubscriptionModals
          showRequestModal={showRequestModal}
          setShowRequestModal={setShowRequestModal}
          selectedTargetPlan={selectedTargetPlan}
          setSelectedTargetPlan={setSelectedTargetPlan}
          targetInterval={targetInterval}
          requestType={requestType}
          requestReason={requestReason}
          setRequestReason={setRequestReason}
          requestComments={requestComments}
          setRequestComments={setRequestComments}
          isSubmittingRequest={isSubmittingRequest}
          requestSuccessMessage={requestSuccessMessage}
          setRequestSuccessMessage={setRequestSuccessMessage}
          handleSubmitSubscriptionRequest={handleSubmitSubscriptionRequest}
          showUpgradeModal={showUpgradeModal}
          setShowUpgradeModal={setShowUpgradeModal}
          selectedUpgradePlan={selectedUpgradePlan}
          isUpgrading={isUpgrading}
          handleConfirmUpgrade={handleConfirmUpgrade}
          showCheckoutPage={showCheckoutPage}
          setShowCheckoutPage={setShowCheckoutPage}
          triggerRazorpayMandate={triggerRazorpayMandate}
          subscription={subscription}
          cycleEndDate={cycleEndDate}
          formatDate={formatDate}
          formatPrice={formatPrice}
        />
      </PageContainer>
    </MainLayout>
  );
};

export default SubscriptionPage;
